/**
 * @pnl/sdk — cliente HTTP tipado consumido pelos 4 frontends.
 *
 * Auto-renovação de access token via refresh em 401. Tokens armazenados via
 * adapter pluggable (localStorage no admin/trader, cookies httpOnly em prod).
 */
import type {
  AccountMetricsResponse,
  AdjustAccountInput,
  AdminAccountDetail,
  AdminResetPasswordInput,
  AuthSession,
  BlackArrowHandoffResponse,
  AdminWithdrawalItem,
  AdminWithdrawalsResponse,
  ListAccountsQuery,
  ListWithdrawalsQuery,
  MarkNotApprovedInput,
  PaginatedAdminAccounts,
  RejectWithdrawalInput,
  ResetAccountInput,
  SetAccountNelogicaIdInput,
  SetClosureNoteInput,
  SetRestartOfferInput,
  SetRestartAvailableInput,
  CampaignSettingsResponse,
  SetCampaignSettingInput,
  RestartPricesResponse,
  SetRestartPricesInput,
  ExcellenceSettings,
  SetExcellenceSettingsInput,
  NewTestBannerSettings,
  SetNewTestBannerInput,
  BannerSettings,
  SetBannerSettingsInput,
  ListNewslettersQuery,
  NewsletterListResponse,
  NewsletterDetail,
  UpsertNewsletterInput,
  PreviewNewsletterInput,
  SendNewsletterInput,
  OperatingWindowsSettings,
  SetOperatingWindowsInput,
  SendTestEmailsInput,
  SendTestEmailsResult,
  AdminPlansResponse,
  ManualActivationInput,
  CalendarResponse,
  ConfirmPasswordResetInput,
  ConsolidatedDashboard,
  CouponListItem,
  CreateCouponInput,
  CheckoutConfigResponse,
  CheckoutStatusResponse,
  CreateUserInput,
  DailySummaryResponse,
  DocumentsResponse,
  EquityCurveResponse,
  ListAuditLogsQuery,
  ListCouponsQuery,
  ListUsersQuery,
  LoginInput,
  NotificationsResponse,
  PaginatedAuditLogs,
  PaginatedCoupons,
  PaginatedUsers,
  PublishTermsInput,
  ReplicatorStatusResponse,
  ReplicatorCheckoutResponse,
  RequestPasswordResetInput,
  ResendVerificationInput,
  RoleItem,
  SignupInput,
  AdminDashboardSummary,
  PayoutsTotalQuery,
  PayoutsTotalResponse,
  RevenueTotalQuery,
  RevenueTotalResponse,
  AdminPlanCatalogResponse,
  AdminTradersResponse,
  ListTradersQuery,
  AdminTraderDossier,
  PayCardInput,
  PayCardResponse,
  StartCheckoutInput,
  StartCheckoutResponse,
  SyncableAccountsResponse,
  TermsListItem,
  TotpActivateInput,
  TotpDisableInput,
  TotpSetupResponse,
  TraderMeResponse,
  TraderCreditsResponse,
  ReferralSettings,
  SetReferralSettingsInput,
  CheckoutFeeSettings,
  SetCheckoutFeeSettingsInput,
  ReferralStatsResponse,
  ReferralCouponItem,
  ReferralCouponsResponse,
  UpdateReferralCouponInput,
  AdminAffiliateItem,
  AdminAffiliatesResponse,
  CreateAffiliateInput,
  UpdateAffiliateInput,
  AdminAffiliateSalesResponse,
  AdminAffiliateWithdrawalItem,
  AdminAffiliateWithdrawalsResponse,
  AffiliateMeResponse,
  AffiliateSalesResponse,
  AffiliateWithdrawalsResponse,
  TraderPendingActivationsResponse,
  TraderPlatformsResponse,
  PlatformCheckoutKind,
  PlatformCheckoutResponse,
  RestartCheckoutResponse,
  TradeDistribution,
  TradesResponse,
  UpdateCouponInput,
  UpdateUserInput,
  UserListItem,
  VerifyEmailInput,
  VirtualAccountSummary,
  WithdrawalsResponse,
} from '@pnl/shared';

export interface ApiError {
  status: number;
  code: string;
  message: string;
  fields?: Record<string, string[]>;
}

export class PnlApiError extends Error {
  constructor(public readonly api: ApiError) {
    super(api.message);
    this.name = 'PnlApiError';
  }
}

export interface TokenStorage {
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  setTokens(access: string, refresh: string): void;
  clear(): void;
}

export interface ClientOptions {
  baseUrl: string;
  storage: TokenStorage;
  /** Chamado quando refresh falha — frontend deve redirecionar pra login. */
  onUnauthorized?: () => void;
}

export function createClient(opts: ClientOptions) {
  let refreshPromise: Promise<string | null> | null = null;

  async function rawFetch(path: string, init: RequestInit, withAuth: boolean): Promise<Response> {
    const headers = new Headers(init.headers);
    if (withAuth) {
      const access = opts.storage.getAccessToken();
      if (access) headers.set('Authorization', `Bearer ${access}`);
    }
    if (init.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    return fetch(`${opts.baseUrl}${path}`, { ...init, headers });
  }

  async function refreshAccess(): Promise<string | null> {
    if (refreshPromise) return refreshPromise;
    const refreshToken = opts.storage.getRefreshToken();
    if (!refreshToken) return null;
    refreshPromise = (async () => {
      try {
        const r = await rawFetch(
          '/auth/refresh',
          { method: 'POST', body: JSON.stringify({ refreshToken }) },
          false,
        );
        if (!r.ok) {
          opts.storage.clear();
          opts.onUnauthorized?.();
          return null;
        }
        const session = (await r.json()) as AuthSession;
        opts.storage.setTokens(session.accessToken, session.refreshToken);
        return session.accessToken;
      } finally {
        refreshPromise = null;
      }
    })();
    return refreshPromise;
  }

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    withAuth = true,
  ): Promise<T> {
    let res = await rawFetch(
      path,
      { method, body: body !== undefined ? JSON.stringify(body) : undefined },
      withAuth,
    );

    if (res.status === 401 && withAuth) {
      const newAccess = await refreshAccess();
      if (newAccess) {
        res = await rawFetch(
          path,
          { method, body: body !== undefined ? JSON.stringify(body) : undefined },
          true,
        );
      } else {
        opts.onUnauthorized?.();
      }
    }

    if (res.status === 204) return undefined as T;

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = (data?.error ?? {}) as Partial<ApiError>;
      throw new PnlApiError({
        status: res.status,
        code: err.code ?? 'UNKNOWN_ERROR',
        message: err.message ?? `HTTP ${res.status}`,
        fields: err.fields,
      });
    }
    return data as T;
  }

  return {
    // Health
    health: () =>
      request<{ status: string; service: string; env: string }>('GET', '/health', undefined, false),

    // Auth — sem token
    signup: (input: SignupInput) =>
      request<{ userId: string; message: string }>('POST', '/auth/signup', input, false),
    login: (input: LoginInput) => request<AuthSession>('POST', '/auth/login', input, false),
    requestPasswordReset: (input: RequestPasswordResetInput) =>
      request<void>('POST', '/auth/password-reset/request', input, false),
    confirmPasswordReset: (input: ConfirmPasswordResetInput) =>
      request<void>('POST', '/auth/password-reset/confirm', input, false),
    verifyEmail: (input: VerifyEmailInput) =>
      request<{ userId: string; message: string }>('POST', '/auth/verify-email', input, false),
    verifyCode: (input: { email: string; code: string }) =>
      request<{ userId: string; message: string }>('POST', '/auth/verify-code', input, false),
    resendVerification: (input: ResendVerificationInput) =>
      request<void>('POST', '/auth/resend-verification', input, false),

    // Auth — sessão
    logout: async () => {
      const refreshToken = opts.storage.getRefreshToken();
      if (refreshToken) {
        await request<void>('POST', '/auth/logout', { refreshToken }, false);
      }
      opts.storage.clear();
    },
    me: () =>
      request<{
        user: {
          id: string;
          email: string;
          fullName: string;
          roles: string[];
          permissions: string[];
          twoFactorEnabled: boolean;
        };
      }>('GET', '/auth/me'),

    // 2FA
    setupTotp: () => request<TotpSetupResponse>('POST', '/auth/totp/setup'),
    activateTotp: (input: TotpActivateInput) => request<void>('POST', '/auth/totp/activate', input),
    disableTotp: (input: TotpDisableInput) => request<void>('POST', '/auth/totp/disable', input),

    // Admin — Users
    listUsers: (query: ListUsersQuery) => {
      const params = new URLSearchParams();
      if (query.q) params.set('q', query.q);
      if (query.status) params.set('status', query.status);
      if (query.role) params.set('role', query.role);
      params.set('page', String(query.page));
      params.set('pageSize', String(query.pageSize));
      return request<PaginatedUsers>('GET', `/admin/users?${params.toString()}`);
    },
    getUser: (id: string) => request<UserListItem>('GET', `/admin/users/${id}`),
    createUser: (input: CreateUserInput) => request<UserListItem>('POST', '/admin/users', input),
    updateUser: (id: string, input: UpdateUserInput) =>
      request<UserListItem>('PATCH', `/admin/users/${id}`, input),
    deleteUser: (id: string) => request<void>('DELETE', `/admin/users/${id}`),
    resetUserPassword: (id: string, input: AdminResetPasswordInput) =>
      request<{ ok: boolean }>('POST', `/admin/users/${id}/reset-password`, input),

    // Admin — Roles
    listRoles: () => request<RoleItem[]>('GET', '/admin/roles'),

    // Checkout (M2.3) — público, sem auth
    getActiveTerms: (market: 'B3' | 'FOREX') =>
      request<{
        id: string;
        version: string;
        contentMd: string;
        publishedAt: string;
      }>('GET', `/checkout/terms/active?market=${market}`, undefined, false),
    getCheckoutConfig: () =>
      request<CheckoutConfigResponse>('GET', '/checkout/config', undefined, false),
    // Preview do cupom (desconto real pro resumo do checkout). 404 = inválido.
    getCouponPreview: (code: string) =>
      request<{ code: string; discountType: 'PERCENT' | 'FIXED'; discountValue: number }>(
        'GET',
        `/checkout/coupon/${encodeURIComponent(code)}`,
        undefined,
        false,
      ),
    startCheckout: (input: StartCheckoutInput) =>
      request<StartCheckoutResponse>('POST', '/checkout/start', input, false),
    /** Cobra no cartão (Mercado Pago) um checkout já iniciado pelo /start. */
    payCheckoutCard: (input: PayCardInput) =>
      request<PayCardResponse>('POST', '/checkout/card', input, false),
    getCheckoutStatus: (subscriptionId: string) =>
      request<CheckoutStatusResponse>(
        'GET',
        `/checkout/subscription/${encodeURIComponent(subscriptionId)}`,
        undefined,
        false,
      ),

    // Admin — Coupons (M2.7)
    listCoupons: (query: ListCouponsQuery) => {
      const params = new URLSearchParams();
      if (query.q) params.set('q', query.q);
      if (query.active !== undefined) params.set('active', String(query.active));
      params.set('page', String(query.page));
      params.set('pageSize', String(query.pageSize));
      return request<PaginatedCoupons>('GET', `/admin/coupons?${params.toString()}`);
    },
    createCoupon: (input: CreateCouponInput) =>
      request<CouponListItem>('POST', '/admin/coupons', input),
    updateCoupon: (id: string, input: UpdateCouponInput) =>
      request<CouponListItem>('PATCH', `/admin/coupons/${id}`, input),
    deleteCoupon: (id: string) => request<void>('DELETE', `/admin/coupons/${id}`),

    // Admin — Terms of Use (M2.8)
    listTerms: () => request<TermsListItem[]>('GET', '/admin/terms'),
    publishTerms: (input: PublishTermsInput) =>
      request<TermsListItem>('POST', '/admin/terms', input),

    // Admin — Audit log
    listAuditLogs: (query: ListAuditLogsQuery) => {
      const params = new URLSearchParams();
      if (query.actorUserId) params.set('actorUserId', query.actorUserId);
      if (query.entity) params.set('entity', query.entity);
      if (query.entityId) params.set('entityId', query.entityId);
      if (query.action) params.set('action', query.action);
      params.set('page', String(query.page));
      params.set('pageSize', String(query.pageSize));
      return request<PaginatedAuditLogs>('GET', `/admin/audit?${params.toString()}`);
    },

    // Admin — Activations (manual VirtualAccount creation post-payment)
    /**
     * Fila de ativações. Por padrão só as SOLICITADAS pelo trader no app; com
     * `all` vêm também as compras que ainda não viraram pedido de ativação.
     * `awaitingRequest` conta sempre essas últimas, independente do filtro.
     */
    listPendingActivations: (opts?: { all?: boolean }) =>
      request<{ items: PendingActivation[]; awaitingRequest: number }>(
        'GET',
        `/admin/activations/pending${opts?.all ? '?all=1' : ''}`,
      ),
    listPendingRestarts: () =>
      request<{ items: PendingRestartItem[] }>('GET', '/admin/restarts/pending'),

    // Admin — metas atingidas aguardando validação manual (a conta não vira
    // funded sozinha; quem promove é a aprovação abaixo)
    listPendingFundedApprovals: () =>
      request<{ items: PendingFundedApproval[] }>('GET', '/admin/funded-approvals/pending'),
    approveFundedPromotion: (accountId: string) =>
      request<{ accountId: string }>('POST', `/admin/funded-approvals/${accountId}/approve`),
    rejectFundedPromotion: (accountId: string, reason: string) =>
      request<{ accountId: string }>('POST', `/admin/funded-approvals/${accountId}/reject`, {
        reason,
      }),
    approveRestartActivation: (accountId: string) =>
      request<{ ok: boolean }>('POST', `/admin/restarts/${accountId}/approve`),
    activateSubscription: (id: string, input: ActivateSubscriptionInput) =>
      request<ActivationResult>('POST', `/admin/subscriptions/${id}/activate`, input),
    getAdminDashboard: () => request<AdminDashboardSummary>('GET', '/admin/dashboard'),
    getPayoutsTotal: (query: PayoutsTotalQuery) => {
      const params = new URLSearchParams();
      if (query.from) params.set('from', query.from);
      if (query.to) params.set('to', query.to);
      const qs = params.toString();
      return request<PayoutsTotalResponse>('GET', `/admin/payouts-total${qs ? `?${qs}` : ''}`);
    },
    getRevenueTotal: (query: RevenueTotalQuery) => {
      const params = new URLSearchParams();
      if (query.from) params.set('from', query.from);
      if (query.to) params.set('to', query.to);
      const qs = params.toString();
      return request<RevenueTotalResponse>('GET', `/admin/revenue-total${qs ? `?${qs}` : ''}`);
    },
    listAdminPlans: () => request<AdminPlansResponse>('GET', '/admin/plans'),
    listPlanCatalog: () => request<AdminPlanCatalogResponse>('GET', '/admin/plans/catalog'),
    listTraders: (query?: ListTradersQuery) => {
      const qs = new URLSearchParams();
      if (query?.q) qs.set('q', query.q);
      if (query?.marketCode) qs.set('marketCode', query.marketCode);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request<AdminTradersResponse>('GET', `/admin/traders${suffix}`);
    },
    /** Dossiê completo do trader, incluindo o que ele vê no app. */
    getTraderDossier: (userId: string) =>
      request<AdminTraderDossier>('GET', `/admin/traders/${userId}/dossier`),
    /** Mesmo dossiê, abrindo por e-mail ou CPF. */
    findTraderDossier: (term: string) =>
      request<AdminTraderDossier>('GET', `/admin/traders/search?q=${encodeURIComponent(term)}`),
    manualActivate: (input: ManualActivationInput) =>
      request<ActivationResult>('POST', '/admin/manual-activations', input),

    // Admin — Contracts
    /**
     * Lista de contratos. Por padrão esconde os checkouts ABANDONADOS (linha
     * criada no início do checkout que nunca virou compra); `abandoned` traz
     * todos. A resposta sempre inclui `abandoned` com quantos ficaram de fora.
     */
    listContracts: (query: ListContractsQuery & { abandoned?: boolean }) => {
      const params = new URLSearchParams();
      if (query.status) params.set('status', query.status);
      params.set('page', String(query.page));
      params.set('pageSize', String(query.pageSize));
      if (query.abandoned) params.set('abandoned', '1');
      return request<PaginatedContracts>('GET', `/admin/contracts?${params.toString()}`);
    },
    resendContract: (id: string) =>
      request<{ contractId: string; to: string; status: ContractStatusKey }>(
        'POST',
        `/admin/contracts/${id}/resend`,
      ),
    /** Gera de uma vez os contratos das compras pagas que ficaram sem documento. */
    retryMissingContracts: () =>
      request<{ attempted: number; ok: number; failed: number; firstError: string | null }>(
        'POST',
        '/admin/contracts/retry-missing',
      ),

    // Admin — Platforms billings
    listPlatformBillings: (query: ListPlatformBillingsQuery) => {
      const params = new URLSearchParams();
      if (query.status) params.set('status', query.status);
      if (query.platform) params.set('platform', query.platform);
      if (query.traderId) params.set('traderId', query.traderId);
      params.set('page', String(query.page));
      params.set('pageSize', String(query.pageSize));
      return request<PaginatedPlatformBillings>(
        'GET',
        `/admin/platforms/billings?${params.toString()}`,
      );
    },
    createPlatformBilling: (input: CreatePlatformBillingInput) =>
      request<PlatformBillingItem>('POST', '/admin/platforms/billings', input),
    markPlatformBillingPaid: (id: string, input: MarkBillingPaidInput) =>
      request<void>('POST', `/admin/platforms/billings/${id}/pay`, input),
    suspendPlatformBilling: (id: string, reason?: string) =>
      request<void>('POST', `/admin/platforms/billings/${id}/suspend`, reason ? { reason } : {}),
    reactivatePlatformBilling: (id: string) =>
      request<void>('POST', `/admin/platforms/billings/${id}/reactivate`),

    // Admin — Replicador (assinaturas pagas)
    listReplicators: (query: ListReplicatorsQuery) => {
      const params = new URLSearchParams();
      if (query.status) params.set('status', query.status);
      params.set('page', String(query.page));
      params.set('pageSize', String(query.pageSize));
      return request<PaginatedReplicators>('GET', `/admin/replicators?${params.toString()}`);
    },
    suspendReplicatorAdmin: (id: string, reason?: string) =>
      request<void>('POST', `/admin/replicators/${id}/suspend`, reason ? { reason } : {}),
    reactivateReplicatorAdmin: (id: string) =>
      request<void>('POST', `/admin/replicators/${id}/reactivate`),

    // Admin — Inadimplência consolidada (assinatura + plataforma + replicador)
    listDelinquencies: () => request<DelinquenciesResponse>('GET', '/admin/delinquencies'),

    // Admin — Fluxo Forex (Pagamento Market Data + avisos)
    generateForexMaintenance: (email: string) =>
      request<{ ok: boolean }>('POST', '/admin/forex/maintenance', { email }),
    sendForexEmail: (email: string, variant: 'd7' | 'd3' | 'd0' | 'late') =>
      request<{ ok: boolean }>('POST', '/admin/forex/emails', { email, variant }),

    // Admin — Contas Virtuais (M3)
    listAdminAccounts: (query: Partial<ListAccountsQuery> = {}) => {
      const params = new URLSearchParams();
      if (query.phase) params.set('phase', query.phase);
      if (query.status) params.set('status', query.status);
      if (query.traderId) params.set('traderId', query.traderId);
      if (query.marketCode) params.set('marketCode', query.marketCode);
      if (query.q) params.set('q', query.q);
      params.set('page', String(query.page ?? 1));
      params.set('pageSize', String(query.pageSize ?? 20));
      return request<PaginatedAdminAccounts>('GET', `/admin/accounts?${params.toString()}`);
    },
    getAdminAccount: (id: string) => request<AdminAccountDetail>('GET', `/admin/accounts/${id}`),
    recalculateAccount: (id: string) =>
      request<AdminAccountDetail>('POST', `/admin/accounts/${id}/recalculate`),
    adjustAccount: (id: string, input: AdjustAccountInput) =>
      request<AdminAccountDetail>('POST', `/admin/accounts/${id}/adjust`, input),
    resetAccount: (id: string, input: ResetAccountInput) =>
      request<AdminAccountDetail>('POST', `/admin/accounts/${id}/reset`, input),
    promoteAccountLive: (id: string) =>
      request<AdminAccountDetail>('POST', `/admin/accounts/${id}/promote-live`),
    markAccountNotApproved: (id: string, input: MarkNotApprovedInput) =>
      request<AdminAccountDetail>('POST', `/admin/accounts/${id}/mark-not-approved`, input),
    setAccountRestartOffer: (id: string, input: SetRestartOfferInput) =>
      request<AdminAccountDetail>('POST', `/admin/accounts/${id}/restart-offer`, input),
    setAccountRestartAvailable: (id: string, input: SetRestartAvailableInput) =>
      request<AdminAccountDetail>('POST', `/admin/accounts/${id}/restart-available`, input),
    setAccountClosureNote: (id: string, input: SetClosureNoteInput) =>
      request<AdminAccountDetail>('POST', `/admin/accounts/${id}/closure-note`, input),
    setAccountNelogicaId: (id: string, input: SetAccountNelogicaIdInput) =>
      request<AdminAccountDetail>('POST', `/admin/accounts/${id}/nelogica-id`, input),

    // Admin — Configurações de campanha
    getCampaignSettings: () =>
      request<CampaignSettingsResponse>('GET', '/admin/settings/campaigns'),
    setCampaignSetting: (input: SetCampaignSettingInput) =>
      request<CampaignSettingsResponse>('PUT', '/admin/settings/campaigns', input),
    getRestartPrices: () => request<RestartPricesResponse>('GET', '/admin/settings/restart-prices'),
    setRestartPrices: (input: SetRestartPricesInput) =>
      request<RestartPricesResponse>('PUT', '/admin/settings/restart-prices', input),
    getExcellenceSettings: () => request<ExcellenceSettings>('GET', '/admin/settings/excellence'),
    setExcellenceSettings: (input: SetExcellenceSettingsInput) =>
      request<ExcellenceSettings>('PUT', '/admin/settings/excellence', input),
    getNewTestBannerSettings: () =>
      request<NewTestBannerSettings>('GET', '/admin/settings/new-test-banner'),
    setNewTestBannerSettings: (input: SetNewTestBannerInput) =>
      request<NewTestBannerSettings>('PUT', '/admin/settings/new-test-banner', input),
    getReferralStats: () => request<ReferralStatsResponse>('GET', '/admin/referrals'),
    getReferralSettings: () => request<ReferralSettings>('GET', '/admin/settings/referral'),
    setReferralSettings: (input: SetReferralSettingsInput) =>
      request<ReferralSettings>('PUT', '/admin/settings/referral', input),
    // Taxas do cartão repassadas por parcela no 12x (checkout v2)
    getCheckoutFeeSettings: () => request<CheckoutFeeSettings>('GET', '/admin/settings/checkout'),
    setCheckoutFeeSettings: (input: SetCheckoutFeeSettingsInput) =>
      request<CheckoutFeeSettings>('PUT', '/admin/settings/checkout', input),
    /** Lista os cupons de indicação (código, trader, % de desconto, usos). */
    listReferralCoupons: () => request<ReferralCouponsResponse>('GET', '/admin/referrals/coupons'),
    /** Ajusta o % de desconto de um cupom de indicação individual. */
    updateReferralCoupon: (id: string, input: UpdateReferralCouponInput) =>
      request<ReferralCouponItem>('PATCH', `/admin/referrals/coupons/${id}`, input),
    getBannerSettings: () => request<BannerSettings>('GET', '/admin/settings/banners'),
    setBannerSettings: (input: SetBannerSettingsInput) =>
      request<BannerSettings>('PUT', '/admin/settings/banners', input),

    // Admin — Newsletter / Comunicados
    listNewsletters: (query: ListNewslettersQuery) => {
      const params = new URLSearchParams();
      if (query.status) params.set('status', query.status);
      params.set('page', String(query.page));
      params.set('pageSize', String(query.pageSize));
      return request<NewsletterListResponse>('GET', `/admin/newsletters?${params.toString()}`);
    },
    getNewsletter: (id: string) => request<NewsletterDetail>('GET', `/admin/newsletters/${id}`),
    createNewsletter: (input: UpsertNewsletterInput) =>
      request<NewsletterDetail>('POST', '/admin/newsletters', input),
    updateNewsletter: (id: string, input: UpsertNewsletterInput) =>
      request<NewsletterDetail>('PATCH', `/admin/newsletters/${id}`, input),
    deleteNewsletter: (id: string) => request<void>('DELETE', `/admin/newsletters/${id}`),
    previewNewsletter: (id: string, input: PreviewNewsletterInput) =>
      request<{ ok: boolean }>('POST', `/admin/newsletters/${id}/preview`, input),
    sendNewsletter: (id: string, input: SendNewsletterInput) =>
      request<NewsletterDetail>('POST', `/admin/newsletters/${id}/send`, input),
    getOperatingWindows: () =>
      request<OperatingWindowsSettings>('GET', '/admin/settings/operating-windows'),
    setOperatingWindows: (input: SetOperatingWindowsInput) =>
      request<OperatingWindowsSettings>('PUT', '/admin/settings/operating-windows', input),
    sendTestEmails: (input: SendTestEmailsInput) =>
      request<SendTestEmailsResult>('POST', '/admin/settings/test-emails', input),

    // Admin — Saques (auditoria humana)
    listAdminWithdrawals: (query: ListWithdrawalsQuery = {}) => {
      const params = new URLSearchParams();
      if (query.status) params.set('status', query.status);
      const qs = params.toString();
      return request<AdminWithdrawalsResponse>('GET', `/admin/withdrawals${qs ? `?${qs}` : ''}`);
    },
    approveWithdrawal: (id: string) =>
      request<AdminWithdrawalItem>('POST', `/admin/withdrawals/${id}/approve`),
    rejectWithdrawal: (id: string, input: RejectWithdrawalInput) =>
      request<AdminWithdrawalItem>('POST', `/admin/withdrawals/${id}/reject`, input),
    markWithdrawalPaid: (id: string) =>
      request<AdminWithdrawalItem>('POST', `/admin/withdrawals/${id}/mark-paid`),
    /** Baixa a nota fiscal anexada a um saque (auditoria admin) como Blob. */
    getAdminWithdrawalInvoiceBlob: async (id: string) => {
      const access = opts.storage.getAccessToken();
      const headers: Record<string, string> = {};
      if (access) headers['Authorization'] = `Bearer ${access}`;
      const r = await fetch(`${opts.baseUrl}/admin/withdrawals/${id}/invoice`, { headers });
      if (!r.ok)
        throw new PnlApiError({
          status: r.status,
          code: 'NOT_FOUND',
          message: 'Nota fiscal indisponível',
        });
      return r.blob();
    },
    /** Baixa o CSV de saques pro financeiro (respeita o filtro de status). */
    downloadWithdrawalsCsv: async (status?: string): Promise<Blob> => {
      const access = opts.storage.getAccessToken();
      const headers: Record<string, string> = {};
      if (access) headers['Authorization'] = `Bearer ${access}`;
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      const r = await fetch(`${opts.baseUrl}/admin/withdrawals/export.csv${qs}`, { headers });
      if (!r.ok)
        throw new PnlApiError({
          status: r.status,
          code: 'EXPORT_FAILED',
          message: 'Falha ao exportar',
        });
      return r.blob();
    },

    // Admin — Programa de Afiliados (permissions affiliates:read / affiliates:write)
    listAffiliates: () => request<AdminAffiliatesResponse>('GET', '/admin/affiliates'),
    createAffiliate: (input: CreateAffiliateInput) =>
      request<AdminAffiliateItem>('POST', '/admin/affiliates', input),
    updateAffiliate: (id: string, input: UpdateAffiliateInput) =>
      request<AdminAffiliateItem>('PATCH', `/admin/affiliates/${id}`, input),
    listAffiliateSales: (id: string) =>
      request<AdminAffiliateSalesResponse>('GET', `/admin/affiliates/${id}/sales`),
    listAffiliateWithdrawals: (id: string) =>
      request<AdminAffiliateWithdrawalsResponse>('GET', `/admin/affiliates/${id}/withdrawals`),
    approveAffiliateWithdrawal: (id: string, withdrawalId: string) =>
      request<AdminAffiliateWithdrawalItem>(
        'POST',
        `/admin/affiliates/${id}/withdrawals/${withdrawalId}/approve`,
      ),
    rejectAffiliateWithdrawal: (id: string, withdrawalId: string, input: RejectWithdrawalInput) =>
      request<AdminAffiliateWithdrawalItem>(
        'POST',
        `/admin/affiliates/${id}/withdrawals/${withdrawalId}/reject`,
        input,
      ),
    markAffiliateWithdrawalPaid: (id: string, withdrawalId: string) =>
      request<AdminAffiliateWithdrawalItem>(
        'POST',
        `/admin/affiliates/${id}/withdrawals/${withdrawalId}/mark-paid`,
      ),
    /** Baixa a nota fiscal anexada a um saque de afiliado (auditoria admin) como Blob. */
    getAffiliateWithdrawalInvoiceBlob: async (id: string, withdrawalId: string) => {
      const access = opts.storage.getAccessToken();
      const headers: Record<string, string> = {};
      if (access) headers['Authorization'] = `Bearer ${access}`;
      const r = await fetch(
        `${opts.baseUrl}/admin/affiliates/${id}/withdrawals/${withdrawalId}/invoice`,
        { headers },
      );
      if (!r.ok)
        throw new PnlApiError({
          status: r.status,
          code: 'NOT_FOUND',
          message: 'Nota fiscal indisponível',
        });
      return r.blob();
    },

    // Admin — CSV Imports (M3)
    listCsvImports: () => request<{ items: CsvImportListItem[] }>('GET', '/admin/csv-imports'),
    getCsvImport: (id: string) => request<CsvImportDetail>('GET', `/admin/csv-imports/${id}`),
    uploadCsv: async (file: File, referenceDate: string, dryRun: boolean) => {
      const form = new FormData();
      form.append('file', file);
      form.append('referenceDate', referenceDate);
      form.append('dryRun', String(dryRun));
      const access = opts.storage.getAccessToken();
      const headers: Record<string, string> = {};
      if (access) headers['Authorization'] = `Bearer ${access}`;
      const r = await fetch(`${opts.baseUrl}/admin/csv-imports/upload`, {
        method: 'POST',
        body: form,
        headers,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const err = (data?.error ?? {}) as Partial<ApiError>;
        throw new PnlApiError({
          status: r.status,
          code: err.code ?? 'UNKNOWN_ERROR',
          message: err.message ?? `HTTP ${r.status}`,
          fields: err.fields,
        });
      }
      return data as CsvIngestResult;
    },

    // Trader (Marco 4) — dashboard data
    getTraderMe: () => request<TraderMeResponse>('GET', '/trader/me'),
    getTraderCredits: () => request<TraderCreditsResponse>('GET', '/trader/credits'),
    getTraderAccounts: () => request<{ items: VirtualAccountSummary[] }>('GET', '/trader/accounts'),
    getTraderPlatforms: () => request<TraderPlatformsResponse>('GET', '/trader/platforms'),
    startPlatformCheckout: (kind: PlatformCheckoutKind) =>
      request<PlatformCheckoutResponse>('POST', `/trader/platforms/${kind}/checkout`),
    startRestartCheckout: (accountId: string) =>
      request<RestartCheckoutResponse>('POST', `/trader/accounts/${accountId}/restart-checkout`),
    makeDefaultAccount: (accountId: string) =>
      request<{ ok: boolean }>('POST', `/trader/accounts/${accountId}/make-default`),
    restartAccount: (accountId: string) =>
      request<{ ok: boolean }>('POST', `/trader/accounts/${accountId}/restart`),
    activateRestart: (accountId: string) =>
      request<{ ok: boolean }>('POST', `/trader/accounts/${accountId}/activate-restart`),
    getAccountMetrics: (accountId: string) =>
      request<AccountMetricsResponse>('GET', `/trader/accounts/${accountId}/metrics`),
    getEquityCurve: (accountId: string) =>
      request<EquityCurveResponse>('GET', `/trader/accounts/${accountId}/equity-curve`),
    getCalendar: (accountId: string, year: number, month: number) =>
      request<CalendarResponse>(
        'GET',
        `/trader/accounts/${accountId}/calendar?year=${year}&month=${month}`,
      ),
    getDailySummary: (accountId: string, limit = 10) =>
      request<DailySummaryResponse>(
        'GET',
        `/trader/accounts/${accountId}/daily-summary?limit=${limit}`,
      ),

    // Trader Marco 4 rodada 2
    getAccountTrades: (
      accountId: string,
      query: {
        page?: number;
        pageSize?: number;
        from?: string;
        to?: string;
        symbol?: string;
        side?: 'BUY' | 'SELL';
      } = {},
    ) => {
      const params = new URLSearchParams();
      if (query.page) params.set('page', String(query.page));
      if (query.pageSize) params.set('pageSize', String(query.pageSize));
      if (query.from) params.set('from', query.from);
      if (query.to) params.set('to', query.to);
      if (query.symbol) params.set('symbol', query.symbol);
      if (query.side) params.set('side', query.side);
      const qs = params.toString();
      return request<TradesResponse>(
        'GET',
        `/trader/accounts/${accountId}/trades${qs ? `?${qs}` : ''}`,
      );
    },
    getConsolidatedDashboard: () =>
      request<ConsolidatedDashboard>('GET', '/trader/dashboard/consolidated'),
    getTradeDistribution: (accountId: string) =>
      request<TradeDistribution>('GET', `/trader/accounts/${accountId}/distribution`),
    /** Baixa o CSV de operações autenticado e devolve um Blob (front cria o download). */
    downloadTradesCsv: async (accountId: string): Promise<Blob> => {
      const access = opts.storage.getAccessToken();
      const headers: Record<string, string> = {};
      if (access) headers['Authorization'] = `Bearer ${access}`;
      const r = await fetch(`${opts.baseUrl}/trader/accounts/${accountId}/trades/export.csv`, {
        headers,
      });
      if (!r.ok) throw new Error('Falha ao exportar CSV');
      return r.blob();
    },
    getTraderNotifications: () => request<NotificationsResponse>('GET', '/trader/notifications'),
    markNotificationRead: (id: string) => request<void>('POST', `/trader/notifications/${id}/read`),
    markAllNotificationsRead: () =>
      request<{ markedCount: number }>('POST', '/trader/notifications/read-all'),
    getTraderDocuments: () => request<DocumentsResponse>('GET', '/trader/documents'),
    traderResendContract: (contractId: string) =>
      request<{ contractId: string; to: string; status: string }>(
        'POST',
        `/trader/contracts/${contractId}/resend`,
      ),

    // Trader Marco 4 rodada 3
    getTraderWithdrawals: () => request<WithdrawalsResponse>('GET', '/trader/withdrawals'),
    requestWithdrawal: async (input: {
      virtualAccountId: string;
      amountCents: number;
      pixKey: string;
      invoice?: File;
    }) => {
      // multipart pra carregar a nota fiscal (opcional) junto da solicitação.
      const form = new FormData();
      form.append('virtualAccountId', input.virtualAccountId);
      form.append('amountCents', String(input.amountCents));
      form.append('pixKey', input.pixKey);
      if (input.invoice) form.append('invoice', input.invoice);
      const access = opts.storage.getAccessToken();
      const headers: Record<string, string> = {};
      if (access) headers['Authorization'] = `Bearer ${access}`;
      const r = await fetch(`${opts.baseUrl}/trader/withdrawals`, {
        method: 'POST',
        body: form,
        headers,
      });
      const data = (await r.json().catch(() => ({}))) as { id?: string; error?: Partial<ApiError> };
      if (!r.ok) {
        const err = data?.error ?? {};
        throw new PnlApiError({
          status: r.status,
          code: err.code ?? 'UNKNOWN_ERROR',
          message: err.message ?? `HTTP ${r.status}`,
        });
      }
      return { id: data.id ?? '' };
    },
    /** Anexa a nota fiscal a um saque JÁ confirmado (fluxo novo). Multipart. */
    attachWithdrawalInvoice: async (id: string, invoice: File) => {
      const form = new FormData();
      form.append('invoice', invoice);
      const access = opts.storage.getAccessToken();
      const headers: Record<string, string> = {};
      if (access) headers['Authorization'] = `Bearer ${access}`;
      const r = await fetch(`${opts.baseUrl}/trader/withdrawals/${id}/invoice`, {
        method: 'POST',
        body: form,
        headers,
      });
      const data = (await r.json().catch(() => ({}))) as { id?: string; error?: Partial<ApiError> };
      if (!r.ok) {
        const err = data?.error ?? {};
        throw new PnlApiError({
          status: r.status,
          code: err.code ?? 'UNKNOWN_ERROR',
          message: err.message ?? `HTTP ${r.status}`,
        });
      }
      return { id: data.id ?? '' };
    },
    /** Baixa a nota fiscal de um saque do trader (autenticado) como Blob. */
    getWithdrawalInvoiceBlob: async (id: string) => {
      const access = opts.storage.getAccessToken();
      const headers: Record<string, string> = {};
      if (access) headers['Authorization'] = `Bearer ${access}`;
      const r = await fetch(`${opts.baseUrl}/trader/withdrawals/${id}/invoice`, { headers });
      if (!r.ok)
        throw new PnlApiError({
          status: r.status,
          code: 'NOT_FOUND',
          message: 'Nota fiscal indisponível',
        });
      return r.blob();
    },
    getReplicatorStatus: () => request<ReplicatorStatusResponse>('GET', '/trader/replicator'),
    startReplicatorCheckout: () =>
      request<ReplicatorCheckoutResponse>('POST', '/trader/replicator/checkout'),
    cancelReplicator: () => request<{ ok: boolean }>('POST', '/trader/replicator/cancel'),
    getSyncableAccounts: () =>
      request<SyncableAccountsResponse>('GET', '/trader/replicator/syncable-accounts'),
    syncReplicatorAccount: (accountId: string) =>
      request<{ ok: boolean }>('POST', `/trader/accounts/${accountId}/replicator-sync`),
    unsyncReplicatorAccount: (accountId: string) =>
      request<{ ok: boolean }>('POST', `/trader/accounts/${accountId}/replicator-unsync`),
    getBlackArrowHandoff: () =>
      request<BlackArrowHandoffResponse>('POST', '/trader/blackarrow/handoff'),

    // Trader — ativação self-service ("Minhas contas")
    getTraderPendingActivations: () =>
      request<TraderPendingActivationsResponse>('GET', '/trader/pending-activations'),
    requestActivation: (subscriptionId: string) =>
      request<{ ok: true }>('POST', `/trader/subscriptions/${subscriptionId}/request-activation`),

    // Área do afiliado (requireAuth + registro Affiliate; 404 se não é afiliado)
    getAffiliateMe: () => request<AffiliateMeResponse>('GET', '/affiliate/me'),
    getAffiliateSales: () => request<AffiliateSalesResponse>('GET', '/affiliate/sales'),
    getAffiliateWithdrawals: () =>
      request<AffiliateWithdrawalsResponse>('GET', '/affiliate/withdrawals'),
    /** Solicita saque do afiliado (multipart — NF opcional; obrigatória acima de R$300). */
    requestAffiliateWithdrawal: async (input: {
      amountCents: number;
      pixKey: string;
      invoice?: File;
    }) => {
      const form = new FormData();
      form.append('amountCents', String(input.amountCents));
      form.append('pixKey', input.pixKey);
      if (input.invoice) form.append('invoice', input.invoice);
      const access = opts.storage.getAccessToken();
      const headers: Record<string, string> = {};
      if (access) headers['Authorization'] = `Bearer ${access}`;
      const r = await fetch(`${opts.baseUrl}/affiliate/withdrawals`, {
        method: 'POST',
        body: form,
        headers,
      });
      const data = (await r.json().catch(() => ({}))) as { id?: string; error?: Partial<ApiError> };
      if (!r.ok) {
        const err = data?.error ?? {};
        throw new PnlApiError({
          status: r.status,
          code: err.code ?? 'UNKNOWN_ERROR',
          message: err.message ?? `HTTP ${r.status}`,
        });
      }
      return { id: data.id ?? '' };
    },
  };
}

// =============================================================================
// Tipos para os novos endpoints admin (activations, contracts, platforms)
// =============================================================================
export interface PendingActivation {
  subscriptionId: string;
  userId: string;
  userEmail: string;
  fullName: string;
  documentNumber: string | null;
  phone: string | null;
  birthDate: string | null;
  planCode: string;
  planName: string;
  marketCode: string;
  paidAt: string | null;
  contractStatus: 'PENDING' | 'SIGNED' | 'REJECTED' | null;
  contractSignedAt: string | null;
  autentiqueDocumentId: string | null;
  /** Setado quando o trader clicou "Ativar plano" na área dele. */
  activationRequestedAt: string | null;
}

export interface PendingRestartItem {
  accountId: string;
  traderId: string;
  traderName: string;
  traderEmail: string;
  accountLabel: string;
  subAccountNelogicaId: string | null;
  type: 'incluso' | 'pago';
  restartsUsed: number;
  blownAt: string | null;
  restartActivationRequested: boolean;
}

/**
 * Conta que bateu a meta do challenge e aguarda VALIDAÇÃO MANUAL do time. Traz
 * os números que o admin precisa conferir antes de promover.
 */
export interface PendingFundedApproval {
  accountId: string;
  label: string;
  traderEmail: string;
  traderFullName: string;
  planName: string;
  marketCode: string;
  metaReachedAt: string;
  capitalCents: number;
  metaCents: number;
  currentBalanceCents: number;
  profitCents: number;
  /** Melhor dia em % do lucro total positivo. */
  consistencyPct: number;
  /** Melhor dia dentro do limite do Challenge (50%). */
  consistencyOk: boolean;
  tradedDays: number;
  /** Maior drawdown usado, ESTÁTICO (contra o ponto zero). */
  drawdownUsedCents: number;
  drawdownLimitCents: number;
}

export interface ActivateSubscriptionInput {
  subAccountNelogicaId: string;
  accountLabel?: string;
  zeroPointCents?: number;
  notes?: string;
}

export interface ActivationResult {
  virtualAccountId: string;
  traderId: string;
  subscriptionId: string;
}

export type ContractStatusKey = 'PENDING' | 'SIGNED' | 'REJECTED';

export interface ContractListItem {
  id: string;
  userId: string;
  userEmail: string;
  userFullName: string;
  subscriptionId: string | null;
  planCode: string | null;
  marketCode: string | null;
  autentiqueDocumentId: string | null;
  /** Tipo do documento: termo da compra ou contrato da conta funded. */
  kind?: 'PURCHASE_TERM' | 'FUNDED_CONTRACT';
  status: ContractStatusKey;
  signedAt: string | null;
  pdfUrl: string | null;
  createdAt: string;
}

export interface ListContractsQuery {
  status?: ContractStatusKey;
  page: number;
  pageSize: number;
}

export interface PaginatedContracts {
  items: ContractListItem[];
  total: number;
  page: number;
  pageSize: number;
  /** Quantos checkouts abandonados existem — contados mesmo quando escondidos. */
  abandoned: number;
  /**
   * Compras PAGAS cujo contrato nunca foi gerado no Autentique. Falha silenciosa
   * que trava o cliente (ele não tem o que assinar) — vira alerta no topo.
   */
  paidWithoutDocument: number;
}

export type PlatformKindKey = 'PROFIT_B3' | 'BLACKARROW_FOREX';
export type PlatformBillingStatusKey = 'PENDING' | 'PAID' | 'OVERDUE' | 'SUSPENDED';

export interface PlatformBillingItem {
  id: string;
  traderId: string;
  traderEmail: string;
  traderFullName: string;
  platform: PlatformKindKey;
  referenceMonth: string;
  status: PlatformBillingStatusKey;
  dueDate: string;
  paidAt: string | null;
  receiptUrl: string | null;
  notes: string | null;
  createdAt: string;
}

export interface ListPlatformBillingsQuery {
  status?: PlatformBillingStatusKey;
  platform?: PlatformKindKey;
  traderId?: string;
  page: number;
  pageSize: number;
}

export interface PaginatedPlatformBillings {
  items: PlatformBillingItem[];
  total: number;
  page: number;
  pageSize: number;
}

// Replicador (assinaturas pagas — admin)
export type ReplicatorStatusKey =
  | 'PENDING_PAYMENT'
  | 'ACTIVE'
  | 'PAST_DUE'
  | 'CANCELED'
  | 'EXPIRED';

export interface ReplicatorAdminItem {
  id: string;
  traderId: string;
  traderEmail: string;
  traderFullName: string;
  status: ReplicatorStatusKey;
  startedAt: string | null;
  canceledAt: string | null;
  nextBillingAt: string | null;
  monthlyAmountCents: number;
  openBillingDueDate: string | null;
}

export interface ListReplicatorsQuery {
  status?: ReplicatorStatusKey;
  page: number;
  pageSize: number;
}

export interface PaginatedReplicators {
  items: ReplicatorAdminItem[];
  total: number;
  page: number;
  pageSize: number;
}

// Inadimplência consolidada (admin)
export type DelinquencyType = 'subscription' | 'platform' | 'replicator';

export interface DelinquencyItem {
  type: DelinquencyType;
  id: string;
  traderName: string;
  traderEmail: string;
  label: string;
  status: string;
  dueDate: string | null;
}

export interface DelinquenciesResponse {
  items: DelinquencyItem[];
  total: number;
}

// CSV Imports (M3)
export interface CsvImportListItem {
  id: string;
  fileName: string;
  fileHash: string;
  referenceDate: string;
  status: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'PARTIAL' | 'FAILED';
  importedAt: string;
  importedBy: string | null;
  errors: unknown;
}

export interface CsvImportDetail extends CsvImportListItem {
  tradeCount: number;
}

export interface CsvAccountImpact {
  virtualAccountId: string;
  label: string;
  prevBalanceCents: number;
  newBalanceCents: number;
  deltaCents: number;
  drawdownUsedCents: number;
  hitMeta: boolean;
  blewDrawdown: boolean;
  dailyLossViolated: boolean;
  phaseAction: 'NONE' | 'META_REACHED' | 'ADVANCE_TO_FUNDED' | 'ADVANCE_TO_LIVE' | 'BLOW_ACCOUNT';
}

export interface CsvIngestResult {
  csvImportId: string | null;
  fileHash: string;
  rowCount: number;
  tradesParsed: number;
  errors: { line: number; reason: string }[];
  uniqueAccounts: string[];
  impacts: CsvAccountImpact[];
  /** Trades fora da janela de operação (M5) — sinalização não-destrutiva. */
  outOfWindowTrades?: number;
  dryRun: boolean;
}

export interface CreatePlatformBillingInput {
  traderId: string;
  platform: PlatformKindKey;
  referenceMonth: string; // YYYY-MM-01
  dueDate: string;
  notes?: string;
}

export interface MarkBillingPaidInput {
  paidAt?: string;
  receiptUrl?: string;
  notes?: string;
}

export type ApiClient = ReturnType<typeof createClient>;

/** Storage padrão usando window.localStorage. */
export function localStorageTokenStorage(prefix = 'pnl_'): TokenStorage {
  return {
    getAccessToken: () => localStorage.getItem(`${prefix}access`),
    getRefreshToken: () => localStorage.getItem(`${prefix}refresh`),
    setTokens: (access, refresh) => {
      localStorage.setItem(`${prefix}access`, access);
      localStorage.setItem(`${prefix}refresh`, refresh);
    },
    clear: () => {
      localStorage.removeItem(`${prefix}access`);
      localStorage.removeItem(`${prefix}refresh`);
    },
  };
}
